/**
 * @file useDateRepricing.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-17
 * @modified 2026-09-30
 * @description When the date that prices a document's lines is changed — a
 *              customer order's delivery date, an invoice's date — offer to
 *              re-price the lines whose contract, spot or milk powder price is
 *              different on the new date. Nothing changes unless the user
 *              accepts.
 *
 *              The screen calls `noteDateChange(from, to)` from the date field
 *              itself, not from a watch on the date: loading a saved document
 *              sets the date too, and must never ask.
 *
 *              The comparison waits for `ready` — the screen's prices, contracts
 *              and spot prices loaded FOR the new date — so it is never made
 *              against the old date's contract list.
 *
 *              The same offer follows a change of CUSTOMER on an order being
 *              edited (`noteCustomerChange`): contracts, spot and milk powder
 *              prices are per customer, so the new customer's are offered the
 *              same way, once `ready` says they have loaded for them. Pass
 *              `customerId` so the check knows when they have.
 *
 *              Usage:
 *                const repricing = useDateRepricing({ variant, lines, setLines,
 *                  date, ready, pricing: { contracts, spotPrices,
 *                  milkPowderContracts, suppliers, periodName },
 *                  inventoryItems, includeCost, lockedLineIds, disabled });
 *                <DateInput onChange={e => { repricing.noteDateChange(date, e.target.value); … }} />
 *                <DatePriceChangesDialog {...repricing.dialogProps} dateLabel="delivery date" />
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { applyRepricing, repriceLinesForDate } from '../utils/lineItems';

export default function useDateRepricing({
  variant = 'order',
  lines,
  setLines,
  date,
  ready,
  pricing = {},
  inventoryItems = [],
  includeCost = false,
  lockedLineIds = null,
  disabled = false,
  customerId = null,
}) {
  const [changes, setChanges] = useState(null);
  const pendingRef = useRef(null);

  const noteDateChange = useCallback((fromDate, toDate) => {
    if (disabled || !fromDate || !toDate || fromDate === toDate) return;
    // Changed again before the first check ran: still compare from where the
    // lines were priced.
    const from = pendingRef.current?.fromDate || fromDate;
    pendingRef.current = from === toDate ? null : { fromDate: from, toDate };
  }, [disabled]);

  // The customer changed on a document whose lines are already priced. Names
  // are for the dialog; the ids decide when the new customer's prices are in.
  const noteCustomerChange = useCallback((fromId, toId, { fromName = '', toName = '' } = {}) => {
    if (disabled || !fromId || !toId || String(fromId) === String(toId)) return;
    const from = pendingRef.current?.kind === 'customer' ? pendingRef.current : null;
    pendingRef.current = from && String(from.fromCustomer) === String(toId)
      ? null
      : {
        kind: 'customer',
        fromCustomer: from ? from.fromCustomer : fromId,
        fromName: from ? from.fromName : fromName,
        toCustomer: toId,
        toName,
      };
  }, [disabled]);

  const { contracts, spotPrices, milkPowderContracts, suppliers, periodName } = pricing;

  useEffect(() => {
    const pending = pendingRef.current;
    if (!pending || !ready) return;
    if (pending.kind === 'customer'
      ? String(pending.toCustomer) !== String(customerId)
      : pending.toDate !== date) return;
    pendingRef.current = null;
    const rows = repriceLinesForDate(lines, {
      variant, inventoryItems, contracts, spotPrices, milkPowderContracts, suppliers,
      deliveryDate: date, periodName, includeCost, lockedLineIds,
    });
    if (rows.length > 0) setChanges({ ...pending, rows });
  }, [ready, date, customerId, lines, variant, inventoryItems, contracts, spotPrices, milkPowderContracts,
      suppliers, periodName, includeCost, lockedLineIds]);

  const accept = useCallback(() => {
    if (!changes) return;
    setLines(prev => applyRepricing(prev, changes.rows));
    setChanges(null);
  }, [changes, setLines]);

  const keep = useCallback(() => setChanges(null), []);

  return {
    noteDateChange,
    noteCustomerChange,
    changes,
    dialogProps: { changes, onAccept: accept, onKeep: keep, includeCost },
  };
}
